import React from 'react'

import ChatTextInput from '../../components/gpt/ChatTextInput'
import Button, { Props as ButtonProps } from '../../components/form/Button'
import { InterviewState } from '../../../api/gpt/types'
import MasterStudioAIPage, { STUDIO_AI_TABS } from './Master'
import { useI18N } from '../../../libraries/i18n/hook'
import Translate from '../../../libraries/i18n/Translate'
import AiDisclaimer from '../../components/ai/Disclaimer'
import ErrorMessage from '../../components/errors/ErrorMessage'
import PlusIcon from '../../../assets/icons/plus.svg?react'
import ToolCard from '../../components/gpt/ToolCard'
import QuestionsWrapper from '../../components/gpt/QuestionsWrapper'
import AudioInterviewStatusRenderer from '../../components/ai-interviewer/AudioInterviewStatusRenderer'
import LoadingMessages from '../../components/ai-interviewer/LoadingMessages'
import { useDismissInterview, useLastActiveInterview, useStartInterview } from './hooks'
import CyberPunkContainer from '../../components/layout/full-screen/CyberPunkContainer'
import { getTopics } from './index'
import { INTERVIEW_TOPICS, Topic } from './types'

const MAX_WIDTH = 'max-w-2xl'
const AiInterview: React.FC = () => {
  const { t } = useI18N()
  const TOPICS = getTopics([
    { value: INTERVIEW_TOPICS.CUSTOM },
    { value: INTERVIEW_TOPICS.BEHAVIOURAL },
    { value: INTERVIEW_TOPICS.FIT },
    { value: INTERVIEW_TOPICS.DATA_SCIENCE },
  ])
  const [selectedTopic, setSelectedTopic] = React.useState<INTERVIEW_TOPICS>(INTERVIEW_TOPICS.CUSTOM)
  const [jobDescription, setJobDescription] = React.useState<string | null>(null)
  const [continueInterview, setContinueInterview] = React.useState<boolean>(false)

  const { data: currentInterview, isLoading: isLoadingCurrentInterview, error: currentInterviewError, mutate: refetchCurrentInterview } = useLastActiveInterview()
  const { trigger: startInterview, isMutating: isStartingInterview, error: errorStartingInterview } = useStartInterview()
  const { trigger: dismissInterview, isMutating: isDismissingInterview, error: errorDismissingInterview } = useDismissInterview()

  const start = async (jdOverride?: string) => {
    const selectedTopicData = TOPICS[selectedTopic]
    const finalJobDescription = jdOverride !== undefined ? jdOverride : jobDescription
    await startInterview({
      topic: selectedTopic,
      numberOfMainQuestions: selectedTopicData?.numberOfQuestions as number,
      numberOfFollowUpQuestions: selectedTopicData?.numberOfFollowUps as number,
      description: finalJobDescription === null ? undefined : finalJobDescription,
    })
    setContinueInterview(true)
  }

  const startWithJobDescription = async (jobDescriptionParam: string) => {
    const trimmedJd = jobDescriptionParam.trim()
    if (!trimmedJd) return
    setJobDescription(trimmedJd)
    await start(trimmedJd)
  }

  const handleTopicChange = (newTopic: INTERVIEW_TOPICS) => {
    setSelectedTopic(newTopic)
  }

  const renderInitialContent = () => {
    const continueInterview = currentInterview?.topic === selectedTopic && currentInterview?.state !== InterviewState.COMPLETED
    const newButton = (
      <Button onClick={continueInterview ? dismissInterviewAndStartNew : () => start()} disabled={isStartingInterview} outlined={continueInterview}>
        {continueInterview ? t('interviewer.ai.start.new') : t('interviewer.ai.start')}
      </Button>
    )
    const continueButton =
      continueInterview ? (
        <Button onClick={continueCurrentInterview}>
          {t('interviewer.ai.continue')}
        </Button>
      ) : <div />

    return (
      <div className={`text-center ${MAX_WIDTH} mx-auto`}>
        <h1 className='mt-6 sm:mt-10 leading-none'>{t('interviewer.ai.title')}</h1>
        <div className='my-5 sm:my-8'>
          <QuestionsWrapper>
            {Object.values(TOPICS).map((topicOption: Topic) => (
              <ToolCard
                key={topicOption.value}
                title={topicOption.label}
                subtitle={topicOption.description}
                onClick={() => handleTopicChange(topicOption.value)}
                isSelected={topicOption.value === selectedTopic}
                inProgress={topicOption.value === currentInterview?.topic && currentInterview?.state !== InterviewState.COMPLETED}
              />
            )
            )}
          </QuestionsWrapper>
        </div>
        <p className='mb-8 text-sm text-gray whitespace-pre-line'>
          <Translate i18nKey='interviewer.ai.description' values={
            {
              context: TOPICS[selectedTopic]?.numberOfQuestions === 1 ? 'singular' : undefined,
              amount: TOPICS[selectedTopic]?.numberOfQuestions || 0,
              expectedTime: TOPICS[selectedTopic]?.expectedTime || '15-20 min'
            }
          } />
        </p>
        {TOPICS[selectedTopic]?.dynamic ? (
          <JobDescriptionBox onSubmitJobDescription={startWithJobDescription} apiError={errorStartingInterview} continueButton={continueButton} newButton={newButton} />
        ) : (
          <div className='my-8 gap-x-4 flex justify-center w-full'>
            {newButton}
            {continueButton}
          </div>
        )}
        {TOPICS[selectedTopic]?.resourcesComponent &&
          <div className={`${MAX_WIDTH}`}>
            {React.createElement(TOPICS[selectedTopic]!.resourcesComponent!)}
          </div>
        }
        <AiDisclaimer />
      </div>
    )
  }

  const onDismissInterview = async () => {
    if(currentInterview) {
      await dismissInterview({ sessionId: currentInterview.session_id })
      await refetchCurrentInterview()
      setContinueInterview(false)
    }
  }

  const dismissInterviewAndStartNew = async () => {
    await onDismissInterview()
    await start()
    setContinueInterview(true)
  }

  const NewInterviewButton = (props: ButtonProps) => (
    <Button icon={<PlusIcon />}  onClick={onDismissInterview} {...props}>
      {t('interviewer.ai.new')}
    </Button>
  )

  const continueCurrentInterview = () => {
    setContinueInterview(true)
  }

  const isLoading = isStartingInterview || isLoadingCurrentInterview || isDismissingInterview
  const apiError = currentInterviewError || errorDismissingInterview || errorStartingInterview

  const interviewInProgress = currentInterview && currentInterview?.state !== InterviewState.INITIAL
  return (
    <MasterStudioAIPage activeTab={STUDIO_AI_TABS.AI_INTERVIEW} buttons={NewInterviewButton({ size: 'lg', outlined: false, variant: 'flat', iconOnlyBelow: 'lg' })}>
      <div className='mt-10 px-4 min-h-[70vh]'>
        {isLoading && <CyberPunkContainer><LoadingMessages color='white' interviewStatus={currentInterview?.state} /></CyberPunkContainer>}
        {!isLoading && interviewInProgress && continueInterview &&
          (
            <AudioInterviewStatusRenderer
              interviewStatus={currentInterview}
              evaluation={currentInterview?.feedback
                ? { decision: currentInterview.decision ?? false, feedback: currentInterview.feedback }
                : undefined
              }
              selectedTopic={selectedTopic}
              error={apiError?.message}
              onClearInterview={onDismissInterview}
            />
          )}
        {!isLoading && !continueInterview && renderInitialContent()}
      </div>
    </MasterStudioAIPage>
  )
}

type JobDescriptionBoxProps = { onSubmitJobDescription: (jobDescription: string) => void, apiError?: string, newButton: React.ReactElement, continueButton: React.ReactElement }
const JobDescriptionBox: React.FC<JobDescriptionBoxProps> = ({ onSubmitJobDescription, apiError, newButton, continueButton }) => {
  const [error, setError] = React.useState('')
  const { t } = useI18N()
  const [jobDescription, setJobDescription] = React.useState('')

  const handleSubmitJD = (e: React.FormEvent) => {
    e.preventDefault()
    if (jobDescription.trim()) {
      onSubmitJobDescription(jobDescription)
    } else {
      setError(t('interviewer.ai.job.description.required'))
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement> ) => { setJobDescription(e.target.value) }

  if(!jobDescription) { <ErrorMessage message='No job description available' /> }

  const customButtons = (
    <div className='my-8 gap-x-4 flex justify-center w-full'>
      {continueButton}
      {React.cloneElement(newButton, {onClick: handleSubmitJD })}
    </div>
  )

  return (
    <>
      <p className='py-2 text-left text-sm font-semibold'>{t('interviewer.ai.custom.resources.title')}</p>
      <p className='text-sm text-gray text-left mb-2'>
        <Translate i18nKey='interviewer.ai.custom.resources.summary'>
          Esta herramienta genera entrevistas a medida a partir de la descripción del puesto para que puedas practicar y mejorar tus respuestas.
        </Translate>
      </p>
      <ChatTextInput
        value={jobDescription}
        onChange={handleInputChange}
        placeholder={t('interviewer.ai.topic.custom-interview.add.job.description')}
        bgColor='gray-300'
        handleSubmit={handleSubmitJD}
        initialRows={5}
        buttonLabel={t('interviewer.ai.start')}
        customButtons={customButtons}
      />
      <ErrorMessage message={(!jobDescription && error) || apiError} />
    </>
  )
}

export default AiInterview
